// -----------------------------------------------------------------------------
// Historique.jsx – history list + inline viewer (card / table)
// -----------------------------------------------------------------------------
import React, { useEffect, useState, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchUserConversations,
  removeConversation,
} from "../../redux/conversationsSlice";
import ProfileTable from "../Profiles/ProfileTable";
import ProfileCard from "../Profiles/ProfileCard";
import ProfileDetails from "../Profiles/ProfileDetails";
import DeleteModal from "../Common/DeleteModal";
import CustomLoader from "../Common/CustomLoader";

import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert as MuiAlert,
} from "@mui/material";
import {
  ArrowBackIos as ArrowBackIosIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
  Sort as SortIcon,
  ArrowBackIosNew as BackIcon,
  ViewModule as GridIcon,
  TableRows as TableIcon,
  Delete as DeleteIcon,
  CalendarToday as CalendarIcon,
  Search as SearchIcon,
  SwapVert as SwapVertIcon,
} from "@mui/icons-material";

import "./styles/Historique.css";

const PRIMARY       = "#2F4E8B";
const ROW_BG        = "#F5F7F9";
const ROW_HOVER     = "#E9EDF1";
const ROW_SELECTED  = "#C9DAF7";

const HIST_PER_PAGE = 5;
const PROF_PER_PAGE = 6;

export default function Historique() {
  const dispatch = useDispatch();
  const { conversations = [], loading, error } = useSelector(
    (s) => s.conversations || {}
  );

  const [sortOrder, setSortOrder]     = useState("desc");
  const [histPage, setHistPage]       = useState(1);
  const [selectedId, setSelectedId]   = useState(null);
  const [viewMode, setViewMode]       = useState("card");
  const [profPage, setProfPage]       = useState(1);
  const [selectedProfile, setSelectedProfile] = useState(null);

  const [delOpen, setDelOpen]         = useState(false);
  const [delId, setDelId]             = useState(null);
  const [snack, setSnack]             = useState({open:false,msg:"",sev:"success"});
  const openSnack = (m,s="success")   => setSnack({open:true,msg:m,sev:s});
  const closeSnack= ()                => setSnack({...snack,open:false});

  const handleCloseDetails = () => setSelectedProfile(null);

  useEffect(() => {
    dispatch(fetchUserConversations());
  }, [dispatch]);

  const sorted = useMemo(() => {
    return [...conversations].sort((a, b) =>
      sortOrder === "desc"
        ? new Date(b.createdAt) - new Date(a.createdAt)
        : new Date(a.createdAt) - new Date(b.createdAt)
    );
  }, [conversations, sortOrder]);

  const histTotal = Math.ceil(sorted.length / HIST_PER_PAGE);
  const histSlice = sorted.slice(
    (histPage - 1) * HIST_PER_PAGE,
    histPage * HIST_PER_PAGE
  );

  const current = useMemo(
    () => sorted.find((c) => c.id === selectedId) || null,
    [sorted, selectedId]
  );

  const profTotal = current
    ? Math.ceil(
        (current.messages?.[0].result?.data || []).length / PROF_PER_PAGE
      )
    : 1;
  const profSlice = current
    ? (current.messages[0].result.data || []).slice(
        (profPage - 1) * PROF_PER_PAGE,
        profPage * PROF_PER_PAGE
      )
    : [];

  useEffect(() => setProfPage(1), [selectedId]);

  const handleDelete = async () => {
    if (!delId) return;
    try {
      await dispatch(removeConversation(delId)).unwrap();
      openSnack("Conversation supprimée", "success");
      if (delId === selectedId) setSelectedId(null);
    } catch {
      openSnack("Échec de la suppression", "error");
    } finally {
      setDelOpen(false);
      setDelId(null);
    }
  };

  const Row = ({ c }) => (
    <Box
      className={`sm-row ${selectedId === c.id ? "sm-selected" : ""}`}
      sx={{
        bgcolor: selectedId === c.id ? ROW_SELECTED : ROW_BG,
        "&:hover": { bgcolor: selectedId === c.id ? ROW_SELECTED : ROW_HOVER },
      }}
      onClick={() => setSelectedId(c.id)}
    >
      <Box className="sm-row-left">
        <Box display="flex" alignItems="center">
          <CalendarIcon sx={{ mr: 1, color: PRIMARY }} />
          <Typography className="sm-title">
            {c.createdAtDay || "—"}
          </Typography>
          <Typography className="sm-date" sx={{ ml: 0.5 }}>
            {c.createdAtHour}
          </Typography>
        </Box>
        <Typography className="sm-filters">
          {c.messages?.[0]?.message || "Message non disponible"}
        </Typography>
      </Box>
      <Box className="sm-actions" onClick={(e) => e.stopPropagation()}>
        <Tooltip title="Voir les résultats">
          <IconButton size="small" onClick={() => setSelectedId(c.id)}>
            <SearchIcon fontSize="small" />
            <Typography
              sx={{ ml: 0.5, fontSize: 13, fontWeight: 600, color: PRIMARY }}
            >
              Voir
            </Typography>
          </IconButton>
        </Tooltip>
        <Tooltip title="Supprimer">
          <IconButton
            size="small"
            onClick={() => {
              setDelId(c.id);
              setDelOpen(true);
            }}
          >
            <DeleteIcon fontSize="small" sx={{ color: "error.main" }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );

  const containerRef = useRef(null);

  // Scroll to top helper (match ProfileList)
  const scrollToTop = () => {
    if (containerRef.current) {
      containerRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  if (loading)
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
        <CustomLoader />
      </Box>
    );
  if (error)
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
        <Typography color="error">{error}</Typography>
      </Box>
    );

  return (
    <Box className="search-management-container" ref={containerRef}>
      {selectedProfile && (
        <Box>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <IconButton onClick={handleCloseDetails}>
              <ArrowBackIosIcon sx={{ color: PRIMARY }} />
            </IconButton>
            <Typography variant="h6" fontWeight={700}>
              Détails du profil
            </Typography>
          </Box>
          <ProfileDetails
            profile={selectedProfile}
            onCloseDetails={handleCloseDetails}
          />
        </Box>
      )}

      {!selectedProfile && (
        <>
          <Box className="sm-topbar" display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={1}>
              <BackIcon
                sx={{ cursor: "pointer", color: PRIMARY }}
                onClick={() =>
                  window.dispatchEvent(
                    new CustomEvent("navigateToComponent", {
                      detail: { component: "profiles" },
                    })
                  )
                }
              />
              <Typography variant="h5" fontWeight={700}>
                Historique
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1} sx={{ cursor: "pointer" }} onClick={() => setSortOrder(sortOrder === "desc" ? "asc" : "desc")}>
              <Tooltip title="Trier par date">
                <SwapVertIcon sx={{ color: PRIMARY }} />
              </Tooltip>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: PRIMARY }}
              >
                ({sortOrder === "desc" ? "▼" : "▲"})
              </Typography>
            </Box>
          </Box>

          {histSlice.map((c) => (
            <Row key={c.id} c={c} />
          ))}

          {histTotal > 1 && (
            <Box className="sm-pagination">
              <IconButton
                onClick={() => {
                  setHistPage((p) => p - 1);
                  setTimeout(scrollToTop, 0);
                }}
                disabled={histPage === 1}
              >
                <ArrowBackIosIcon sx={{ color: PRIMARY }} />
              </IconButton>
              <Box className="sm-page-bubble">{histPage}</Box>
              <IconButton
                onClick={() => {
                  setHistPage((p) => p + 1);
                  setTimeout(scrollToTop, 0);
                }}
                disabled={histPage === histTotal}
              >
                <ArrowForwardIosIcon sx={{ color: PRIMARY }} />
              </IconButton>
            </Box>
          )}

          {!current && (
            <Typography sx={{ mt: 3, fontStyle: "italic", color: "#6e7a8a" }}>
              Sélectionnez une conversation pour voir les résultats.
            </Typography>
          )}

          {current && (
            <Box sx={{ mt: 4 }}>
              <Box className="sm-res-bar">
                <Typography fontWeight={700}>Résultat de la conversation</Typography>
                <Box className="sm-view-btn">
                  <Tooltip title="Mode cartes">
                    <IconButton data-active={viewMode === "card"} onClick={() => setViewMode("card")}>
                      <GridIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Mode tableau">
                    <IconButton data-active={viewMode === "table"} onClick={() => setViewMode("table")}>
                      <TableIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {viewMode === "card" ? (
                <Box className="sm-card-grid">
                  {profSlice.map((p) => (
                    <ProfileCard key={p.id} profile={p} onClick={() => setSelectedProfile(p)} />
                  ))}
                </Box>
              ) : (
                <ProfileTable rows={profSlice} onSelect={(p) => setSelectedProfile(p)} />
              )}

              {profTotal > 1 && (
                <Box className="sm-pagination">
                  <IconButton
                    onClick={() => {
                      setProfPage((p) => p - 1);
                      setTimeout(scrollToTop, 0);
                    }}
                    disabled={profPage === 1}
                  >
                    <ArrowBackIosIcon sx={{ color: PRIMARY }} />
                  </IconButton>
                  <Box className="sm-page-bubble">{profPage}</Box>
                  <IconButton
                    onClick={() => {
                      setProfPage((p) => p + 1);
                      setTimeout(scrollToTop, 0);
                    }}
                    disabled={profPage === profTotal}
                  >
                    <ArrowForwardIosIcon sx={{ color: PRIMARY }} />
                  </IconButton>
                </Box>
              )}
            </Box>
          )}
        </>
      )}

      <DeleteModal
        open={delOpen}
        onClose={() => setDelOpen(false)}
        onConfirm={handleDelete}
        message="Êtes‑vous sûr ?"
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={closeSnack}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <MuiAlert onClose={closeSnack} severity={snack.sev}>
          {snack.msg}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
}
